<script setup lang="ts">
/**
 * The app frame: sidebar (lg+), glassy sticky top bar, one page container
 * (layout.ts) and, below lg, a bottom tab bar with a "More" drawer.
 */
import { Search } from "@lucide/vue";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import NoAccess from "@/components/common/NoAccess.vue";
import NotificationBell from "@/components/notifications/NotificationBell.vue";
import SchorschiPanel from "@/components/schorschi/SchorschiPanel.vue";
import { Kbd } from "@/components/ui/kbd";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import { cn } from "@/lib/utils";
import AppNav from "./AppNav.vue";
import BrandMark from "./BrandMark.vue";
import CommandPalette from "./CommandPalette.vue";
import { frameClass, GUTTER, NARROW, pageLayout } from "./layout";
import MobileTabBar from "./MobileTabBar.vue";
import UserMenu from "./UserMenu.vue";

const route = useRoute();
const { allows } = useSession();
const mobileOpen = ref(false);
const paletteOpen = ref(false);

const permitted = computed(() => route.matched.every((r) => allows(r.meta.requires)));
const layout = computed(() => pageLayout(route.meta));
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
</script>

<template>
  <a
    href="#main"
    class="sr-only z-50 rounded-md bg-primary px-3 py-2 text-primary-foreground focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
  >
    {{ t.app.skip }}
  </a>
  <div class="min-h-dvh lg:grid lg:grid-cols-[15rem_minmax(0,1fr)]">
    <!-- Desktop sidebar -->
    <aside class="sticky top-0 hidden h-dvh flex-col border-r border-sidebar-border bg-sidebar lg:flex">
      <div class="flex h-14 shrink-0 items-center px-4">
        <RouterLink to="/" class="-mx-1.5 rounded-md px-1.5 py-1 transition-opacity hover:opacity-85 active:opacity-70">
          <BrandMark />
        </RouterLink>
      </div>
      <div class="flex-1 overflow-y-auto overscroll-contain px-2.5 pt-1 pb-4">
        <AppNav />
      </div>
    </aside>

    <div class="flex min-w-0 flex-col">
      <!-- Top bar -->
      <header
        :class="
          cn(
            'sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border/70 bg-background/80 backdrop-blur-xl backdrop-saturate-150 supports-[backdrop-filter]:bg-background/65',
            GUTTER,
          )
        "
      >
        <RouterLink to="/" class="-ml-1 shrink-0 rounded-md p-1 lg:hidden" :aria-label="t.nav.home">
          <BrandMark compact />
        </RouterLink>
        <button
          type="button"
          class="group flex h-9 min-w-0 flex-1 items-center gap-2.5 rounded-lg border border-input bg-control/70 px-3 text-sm text-muted-foreground shadow-xs transition-[background-color,border-color,box-shadow] duration-150 hover:border-border-strong hover:bg-control hover:text-foreground active:bg-control-press sm:max-w-md"
          :aria-label="t.common.search"
          aria-haspopup="dialog"
          @click="paletteOpen = true"
        >
          <Search class="size-4 shrink-0 transition-colors group-hover:text-foreground" aria-hidden="true" />
          <span class="truncate">{{ t.knowledge.searchPlaceholder }}</span>
          <Kbd class="ml-auto hidden sm:inline-flex">{{ isMac ? "⌘" : "Ctrl" }} K</Kbd>
        </button>
        <div class="ml-auto flex shrink-0 items-center gap-1">
          <NotificationBell />
          <UserMenu />
        </div>
      </header>

      <main id="main" tabindex="-1" class="flex-1 pb-(--tabbar-offset) outline-none">
        <div :class="frameClass(layout)" :data-layout="layout">
          <RouterView v-slot="{ Component }">
            <NoAccess v-if="!permitted" />
            <div v-else-if="layout === 'narrow'" :class="NARROW">
              <component :is="Component" />
            </div>
            <component :is="Component" v-else />
          </RouterView>
        </div>
      </main>
    </div>
  </div>

  <!-- Phones and tablets: bottom tab bar; "More" opens the full navigation -->
  <MobileTabBar :more-open="mobileOpen" @more="mobileOpen = true" />
  <Sheet v-model:open="mobileOpen">
    <SheetContent side="left" class="w-[min(20rem,calc(100vw-3rem))] gap-0 bg-sidebar p-0">
      <SheetHeader class="h-14 shrink-0 flex-row items-center border-b px-4">
        <SheetTitle><BrandMark /></SheetTitle>
        <SheetDescription class="sr-only">{{ t.common.menu }}</SheetDescription>
      </SheetHeader>
      <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pt-3 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
        <AppNav touch @navigate="mobileOpen = false" />
      </div>
    </SheetContent>
  </Sheet>

  <CommandPalette v-model:open="paletteOpen" />
  <SchorschiPanel />
</template>
